import React, { useState, useEffect } from 'react';
import { adminAPI } from '../../services/api';
import { AdminUser, LoginHistory } from '../../types';
import {
  Users,
  Search,
  Trash2,
  Clock,
  AlertCircle,
  ChevronLeft,
  ChevronRight,
  Shield
} from 'lucide-react';

export const UserManagement: React.FC = () => {
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [selectedUser, setSelectedUser] = useState<AdminUser | null>(null);
  const [loginHistory, setLoginHistory] = useState<LoginHistory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState('');

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await adminAPI.getUsers(page, 10, search);
      setUsers(response.users);
      setTotalPages(response.total_pages);
    } catch (err: any) {
      setError(err.message || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const fetchLoginHistory = async (userId: number) => {
    try {
      const response = await adminAPI.getUserLoginHistory(userId);
      setLoginHistory(response.logs);
    } catch (err: any) {
      console.error('Failed to load login history:', err);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, [page, search]);

  const handleUserSelect = async (user: AdminUser) => {
    setSelectedUser(user);
    await fetchLoginHistory(user.id);
  };

  const handleUserDelete = async (userId: number) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;
    try {
      await adminAPI.deleteUser(userId);
      fetchUsers();
      if (selectedUser?.id === userId) {
        setSelectedUser(null);
        setLoginHistory([]);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to delete user');
    }
  };

  if (loading && !users.length) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="loading-spinner h-8 w-8"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-red-500 flex items-center">
          <AlertCircle className="w-5 h-5 mr-2" />
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 p-6">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <h1 className="text-3xl font-bold text-white">User Management</h1>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search users..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 pr-4 py-2 bg-gray-800 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* User List */}
          <div className="lg:col-span-2">
            <div className="bg-gray-800 rounded-lg overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-gray-700">
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">User</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Status</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Role</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-700">
                    {(Array.isArray(users) && users.length > 0) ? (
                      users.map((user) => (
                        <tr
                          key={user.id}
                          className={`hover:bg-gray-700/50 cursor-pointer ${selectedUser?.id === user.id ? 'bg-gray-700/50' : ''}`}
                          onClick={() => handleUserSelect(user)}
                        >
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <div>
                                <div className="text-sm font-medium text-white">{user.username}</div>
                                <div className="text-sm text-gray-400">{user.email}</div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`px-2 py-1 text-xs rounded-full ${
                              user.status === 'active' ? 'bg-green-900/20 text-green-400' :
                              user.status === 'banned' ? 'bg-red-900/20 text-red-400' :
                              'bg-gray-900/20 text-gray-400'
                            }`}>
                              {user.status}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`px-2 py-1 text-xs rounded-full ${
                              user.role === 'admin' ? 'bg-purple-900/20 text-purple-400' :
                              'bg-blue-900/20 text-blue-400'
                            }`}>
                              {user.role}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-400">
                            <div className="flex items-center space-x-2">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleUserDelete(user.id);
                                }}
                                className="text-red-400 hover:text-red-300"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={4} className="text-center text-gray-400 py-8">
                          No users found.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              <div className="bg-gray-700 px-4 py-3 flex items-center justify-between border-t border-gray-600">
                <div className="flex-1 flex justify-between sm:hidden">
                  <button
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="relative inline-flex items-center px-4 py-2 border border-gray-600 text-sm font-medium rounded-md text-gray-300 bg-gray-700 hover:bg-gray-600"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                    className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-600 text-sm font-medium rounded-md text-gray-300 bg-gray-700 hover:bg-gray-600"
                  >
                    Next
                  </button>
                </div>
                <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                  <div>
                    <p className="text-sm text-gray-400">
                      Showing page <span className="font-medium">{page}</span> of{' '}
                      <span className="font-medium">{totalPages}</span>
                    </p>
                  </div>
                  <div>
                    <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                      <button
                        onClick={() => setPage(p => Math.max(1, p - 1))}
                        disabled={page === 1}
                        className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-600 bg-gray-700 text-sm font-medium text-gray-300 hover:bg-gray-600"
                      >
                        <ChevronLeft className="w-5 h-5" />
                      </button>
                      <button
                        onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                        disabled={page === totalPages}
                        className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-600 bg-gray-700 text-sm font-medium text-gray-300 hover:bg-gray-600"
                      >
                        <ChevronRight className="w-5 h-5" />
                      </button>
                    </nav>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* User Details */}
          <div className="lg:col-span-1">
            {selectedUser ? (
              <div className="bg-gray-800 rounded-lg p-6">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-lg font-semibold text-white">User Details</h3>
                  <Shield className="w-5 h-5 text-blue-400" />
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-400">Username</label>
                    <div className="mt-1 text-white">{selectedUser.username}</div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-400">Email</label>
                    <div className="mt-1 text-white">{selectedUser.email}</div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-400">Status</label>
                    <div className="mt-1">
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        selectedUser.status === 'active' ? 'bg-green-900/20 text-green-400' :
                        selectedUser.status === 'banned' ? 'bg-red-900/20 text-red-400' :
                        'bg-gray-900/20 text-gray-400'
                      }`}>
                        {selectedUser.status}
                      </span>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-400">Role</label>
                    <div className="mt-1">
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        selectedUser.role === 'admin' ? 'bg-purple-900/20 text-purple-400' :
                        'bg-blue-900/20 text-blue-400'
                      }`}>
                        {selectedUser.role}
                      </span>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-400">Created</label>
                    <div className="mt-1 text-white">{new Date(selectedUser.created_at).toLocaleString()}</div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-400">Last Login</label>
                    <div className="mt-1 text-white">{new Date(selectedUser.last_login).toLocaleString()}</div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-400">Login Count</label>
                    <div className="mt-1 text-white">{selectedUser.login_count}</div>
                  </div>
                </div>

                <div className="mt-8">
                  <h4 className="text-sm font-medium text-gray-400 mb-4 flex items-center">
                    <Clock className="w-4 h-4 mr-2" />
                    Recent Login History
                  </h4>
                  <div className="space-y-3">
                    {(Array.isArray(loginHistory) && loginHistory.length > 0) ? (
                      loginHistory.map((log) => (
                        <div key={log.id} className="bg-gray-700/50 rounded-lg p-3">
                          <div className="flex items-center justify-between text-sm">
                            <span className={`px-2 py-1 rounded-full text-xs ${
                              log.status === 'success' ? 'bg-green-900/20 text-green-400' : 'bg-red-900/20 text-red-400'
                            }`}>
                              {log.status}
                            </span>
                            <span className="text-gray-400">{new Date(log.created_at).toLocaleString()}</span>
                          </div>
                          <div className="mt-2 text-xs text-gray-400">
                            <div>IP: {log.ip_address}</div>
                            <div className="truncate">User Agent: {log.user_agent}</div>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="text-center text-gray-400 py-4">
                        No login history found.
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <div className="bg-gray-800 rounded-lg p-6 flex items-center justify-center h-full">
                <div className="text-center text-gray-400">
                  <Users className="w-8 h-8 mx-auto mb-2" />
                  <p>Select a user to view details</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}; 